import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowUpRight } from "lucide-react";
import ArticleCover from "@/components/ArticleCover";

export default function Articles() {
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    // Fetch once on mount; api / setters are stable module-level refs.
    api.get("/articles").then((r) => setArticles(r.data)).catch(() => {});
  }, []);

  const featured = articles[0];
  const rest = articles.slice(1);

  return (
    <div className="bg-white" data-testid="articles-page">
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <div className="label-mono">Insights · Blog</div>
          <h1 className="font-display text-5xl sm:text-6xl font-semibold mt-3 tracking-[-0.04em] leading-[0.98]">Field notes &amp; playbooks.</h1>
          <p className="text-muted-foreground mt-5 max-w-2xl leading-relaxed text-lg">
            Industry analysis, operating playbooks, and short writeups from active engagements. Published when we have something useful to say.
          </p>
        </div>
      </section>

      {featured && (
        <section className="border-b border-border">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
            <Link to={`/articles/${featured.slug}`} className="grid lg:grid-cols-12 gap-10 items-center group" data-testid="article-featured">
              <div className="lg:col-span-7 aspect-[16/10] rounded-2xl overflow-hidden">
                <ArticleCover article={featured} className="w-full h-full transition-transform duration-700 group-hover:scale-105" size="lg" />
              </div>
              <div className="lg:col-span-5">
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-[#ac62ea] font-medium uppercase tracking-widest">{featured.category}</span>
                </div>
                <h2 className="font-display text-3xl sm:text-4xl mt-4 font-semibold tracking-[-0.03em] leading-tight">{featured.title}</h2>
                <p className="text-muted-foreground mt-4 leading-relaxed">{featured.subtitle}</p>
                <div className="mt-7 inline-flex items-center gap-1.5 text-[#ac62ea] font-medium text-sm group-hover:underline">
                  Read article <ArrowUpRight className="h-4 w-4" />
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      <section>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {rest.map((a, i) => (
              <Link to={`/articles/${a.slug}`} key={a.id} className="card-sharp overflow-hidden flex flex-col group" data-testid={`article-list-${i}`}>
                <div className="aspect-[16/10] overflow-hidden">
                  <ArticleCover article={a} className="w-full h-full transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-[#ac62ea] font-medium uppercase tracking-widest">{a.category}</span>
                  </div>
                  <h3 className="font-display text-xl mt-3 font-semibold tracking-tight leading-snug">{a.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed line-clamp-2 flex-1">{a.subtitle}</p>
                  <div className="mt-5 pt-4 border-t border-border flex justify-between text-xs text-muted-foreground">
                    <span>{a.author}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
